// useShareLinkAttribution.ts — CORE-788
//
// The inbound half of the share funnel: somebody opened a link that came out of
// a «Поделиться» somewhere. `buildShareUrl` stamps `?ref=share` on every link it
// produces; this hook reads the marker once per app start and reports it.
//
// Without it the report answers only «жали ли кнопку». Whether sharing brings
// anyone in — the question actually worth asking — needs this side.

import { useEffect, useRef } from 'react';
import { Linking, Platform } from 'react-native';
import {
  SHARE_REF_PARAM,
  SHARE_REF_VALUE,
  entityFromSharePath,
  trackShareLinkOpened,
} from '../services/shareAnalytics';

/**
 * Split a full URL into path + query without relying on a `URL` global.
 *
 * Three shapes reach this: a bare web path, a universal link
 * (`https://app.agapella.io/room/9`) and a custom-scheme deep link
 * (`agapella://room/9`). The last one is the reason this is hand-rolled —
 * generic URL parsing reads `room` as the *host* there and leaves `/9` as the
 * path, which resolves to no route and silently drops the row. For a
 * non-http scheme everything after `://` is the path.
 */
export function splitUrl(href: string): { pathname: string; search: string } {
  const withoutHash = href.split('#')[0];
  const qIndex = withoutHash.indexOf('?');
  const search = qIndex === -1 ? '' : withoutHash.slice(qIndex + 1);
  const beforeQuery = qIndex === -1 ? withoutHash : withoutHash.slice(0, qIndex);

  const schemeEnd = beforeQuery.indexOf('://');
  if (schemeEnd === -1) return { pathname: beforeQuery || '/', search };

  const scheme = beforeQuery.slice(0, schemeEnd).toLowerCase();
  const afterScheme = beforeQuery.slice(schemeEnd + 3);

  if (scheme === 'http' || scheme === 'https') {
    const slash = afterScheme.indexOf('/');
    return { pathname: slash === -1 ? '/' : afterScheme.slice(slash), search };
  }

  return { pathname: afterScheme.startsWith('/') ? afterScheme : `/${afterScheme}`, search };
}

/**
 * Report the opening of a shared link, if this is one.
 *
 * Fires at most once per app start, and only after auth has settled — the
 * `isAuthenticated` flag is the whole point of the row (a shared link that
 * lands on a logged-out stranger is the interesting case), and reading it
 * mid-restore would record a guest for every returning user.
 */
export function useShareLinkAttribution(isAuthenticated: boolean, isLoading: boolean): void {
  const fired = useRef(false);

  useEffect(() => {
    if (isLoading || fired.current) return;

    let cancelled = false;
    const report = (href?: string | null) => {
      if (cancelled || fired.current || !href) return;

      const { pathname, search } = splitUrl(href);
      if (new URLSearchParams(search).get(SHARE_REF_PARAM) !== SHARE_REF_VALUE) return;

      // A path outside the share route table yields nothing rather than an
      // «unknown» row: an unrelated link that happens to carry `?ref=share`
      // must not be able to invent an entity.
      const entity = entityFromSharePath(pathname, search);
      if (!entity) return;

      fired.current = true;
      trackShareLinkOpened({
        entityType: entity.entityType,
        entityId: entity.entityId,
        isAuthenticated,
      });
    };

    if (Platform.OS === 'web') {
      if (typeof window === 'undefined') return;
      report(`${window.location?.pathname || ''}${window.location?.search || ''}`);
      return;
    }

    Linking.getInitialURL().then(report).catch(() => { /* no deep link */ });
    return () => { cancelled = true; };
  }, [isAuthenticated, isLoading]);
}
